//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useState } from 'react';
import { makeStyles } from '@material-ui/core';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { AFITD_SHARE_URL } from '../../system/constants';
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { EMAIL_REGEX } from '../../common/constants';
import { PLACEHOLDER_FIELD_SETTINGS_USERS_INVITE, PLACEHOLDER_FIELD_SETTINGS_USERS_INVITE_OUT_OF_SEATS } from '../../common/placeholderHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { sendInvitation } from '../../common/services/empMySettingsServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdButton from '../../common/elemental/AfitdButton';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        searchInput: { display: 'flex', height: '48px', background: 'transparent', borderRadius: '4px', border: '1px solid #D5D7DD' },
        errorSearchInput: { display: 'flex', height: '48px', background: 'transparent', borderRadius: '4px', border: '2px solid red' },
        roleContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' },
        inputSide: { flex: 1 },
        input: { backgroundColor: 'transparent', border: 'none', height: '48px', fontSize: 16, outline: 'none', flex: 1, margin: '0 15px' },
        optionsIcon: { fontSize: 22, margin: '0 10px' },

        inviteRole: { fontWeight: 'bold' },
        container: { display: 'flex', alignItems: 'center', margin: '20px 0' },
        emailFieldContainer: { flexGrow: 1 },
        inviteButton: { margin: '0 10px' },

        hidden: { display: 'none' },
        // errorMessage: { color: 'red' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, email, setEmail, role, setRole, handleInviteButton, isValidEmail, setIsValidEmail, props } = params;
    const { hidden: hiddenCls, errorSearchInput: errorSearchInputCls, searchInput: searchInputCls, roleContainer: roleContainerCls } = classes;

    const { isAdmin, vacantSeats = 0 } = props;

    const isDirty = email;
    const outOfSeats = vacantSeats <= 0;

    const inputPlaceholder = outOfSeats ? PLACEHOLDER_FIELD_SETTINGS_USERS_INVITE_OUT_OF_SEATS : PLACEHOLDER_FIELD_SETTINGS_USERS_INVITE;

    const handleEmailChange = evt => {
        const { value } = evt.target;
        setEmail(value);
        !value && setIsValidEmail(true);
    };

    const defns = {
        searchInput: { className: isValidEmail ? searchInputCls : errorSearchInputCls },
        input: { disabled: !isAdmin || outOfSeats, type: 'text', placeholder: inputPlaceholder, value: email, onChange: handleEmailChange },
        inviteRole: { variant: 'body2', value: role },
        roleContainer: { onClick: () => setRole(role === 'Admin' ? 'User' : 'Admin'), className: !isAdmin ? hiddenCls : roleContainerCls },
        inviteButton: { disabled: !isDirty || !isAdmin || outOfSeats, onClick: handleInviteButton, color: 'secondary', label: 'Add Member', size: 'large' },

        errorMessage: { variant: 'body2', value: 'Please enter a vaild email address', className: isValidEmail && hiddenCls },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const InviteSection = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();

    const appContext = useContext(AppContext);
    const { currentEmployer, employerId, userId } = appContext;
    const members = _get(currentEmployer, 'members', []);
    const invited = _get(currentEmployer, 'invited', []);

    const layoutContext = useContext(LayoutContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { isHidden } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('User');
    const [isValidEmail, setIsValidEmail] = useState(true);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleInviteButton = () => {
        if (email.match(EMAIL_REGEX)) {
            setIsValidEmail(true);
            sendInvitation({
                email,
                existingInvited: invited,
                employerId,
                existingMembers: members,
                inviterUserId: userId,
                isAdmin: role === 'Admin',
                callback: ({ code } = {}) => {
                    // //FIXME temporarily display invitation link for testers
                    // alert(`https://${AFITD_SHARE_URL}/ei/${code}`);
                    setEmail('');
                },
                setSnackbarMessage,
                setSnackbarErrorMessage,
            });
        } else {
            setIsValidEmail(false);
        }
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    if (isHidden) return null;
    const defns = getDefns({ classes, props, handleInviteButton, email, setEmail, role, setRole, isValidEmail, setIsValidEmail });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <>
            <div {...defns.container}>
                <div {...defns.emailFieldContainer}>
                    <div {...defns.searchInput}>
                        <input {...defns.input} />
                        <div {...defns.roleContainer}>
                            <AfitdTypography {...defns.inviteRole} />
                            <ArrowDropDownIcon {...defns.optionsIcon} />
                        </div>
                    </div>
                </div>
                <AfitdButton {...defns.inviteButton} />
            </div>
            <AfitdTypography {...defns.errorMessage} />
        </>
    );
};

export default InviteSection;
